import type { EmittersData } from '../types';

const EMITTER_LABELS: Record<string, string> = {
  radiators: 'Radiators',
  ufh: 'UFH',
  mixed: 'Mixed (rads + UFH)',
  unknown: 'Unknown',
};

const PERF_LABELS: Record<string, string> = {
  'all-heat-well': 'Rooms heat well',
  'some-struggle': 'Some rooms struggle',
  'many-struggle': 'Many rooms struggle',
  unknown: 'Unknown',
};

const SIZE_LABELS: Record<string, string> = {
  '15mm': '15 mm',
  '22mm': '22 mm',
  '28mm': '28 mm',
  unknown: 'Unknown',
};

const LAYOUT_LABELS: Record<string, string> = {
  microbore: 'Microbore',
  'one-pipe': 'One-pipe',
  'two-pipe': 'Two-pipe',
  unknown: 'Unknown',
};

const SEP_LABELS: Record<string, string> = {
  none: 'None',
  llh: 'Low-loss header',
  buffer: 'Buffer vessel',
  'plate-hx': 'Plate HX',
  unknown: 'Unknown',
};

interface Props {
  data: EmittersData;
  onClick: () => void;
}

export function EmittersTile({ data, onClick }: Props) {
  const v = (val: string, map: Record<string, string>) => map[val] ?? val ?? '—';
  const isUnknown = (val: string) => !val || val === 'unknown';

  const lowTempLabel = data.lowTempCapability === 'yes' ? 'Yes' : data.lowTempCapability === 'no' ? 'No' : 'Unknown';

  return (
    <div className="hub-tile" onClick={onClick} role="button" tabIndex={0}>
      <div className="hub-tile__header">
        <h3 className="hub-tile__title">Emitters & Distribution</h3>
        <span className={`hub-tile__badge hub-tile__badge--${data.confidence.level}`}>
          {data.confidence.level}
        </span>
      </div>
      <div className="hub-tile__body">
        <div className="hub-tile__row">
          <span className="hub-tile__label">Emitters</span>
          <span className={`hub-tile__value${isUnknown(data.emitters) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.emitters, EMITTER_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Performance</span>
          <span className={`hub-tile__value${isUnknown(data.performanceSignal) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.performanceSignal, PERF_LABELS)}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Low-temp</span>
          <span className={`hub-tile__value${isUnknown(data.lowTempCapability) ? ' hub-tile__value--unknown' : ''}`}>
            {lowTempLabel}
          </span>
        </div>
        <div className="hub-tile__row">
          <span className="hub-tile__label">Primaries</span>
          <span className={`hub-tile__value${isUnknown(data.primarySizeBand) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.primarySizeBand, SIZE_LABELS)}
          </span>
        </div>
        {data.layoutType && data.layoutType !== 'unknown' && (
          <div className="hub-tile__row">
            <span className="hub-tile__label">Layout</span>
            <span className="hub-tile__value">{v(data.layoutType, LAYOUT_LABELS)}</span>
          </div>
        )}
        <div className="hub-tile__row">
          <span className="hub-tile__label">Hyd. separation</span>
          <span className={`hub-tile__value${isUnknown(data.hydraulicSeparation) ? ' hub-tile__value--unknown' : ''}`}>
            {v(data.hydraulicSeparation, SEP_LABELS)}
          </span>
        </div>
      </div>

      {data.hydraulicSeparation === 'llh' && (
        <div className="hub-tile__warnings">
          <span className="hub-tile__warning">LLH protects pumps, not performance</span>
        </div>
      )}

      <span className="hub-tile__tap-hint">tap to edit</span>
    </div>
  );
}
